import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { loginSuccess } from "../../store/authSlice";
import {
  resetMapsState,
  fetchMaps,
  fetchSharedMaps,
} from "../../store/mapsSlice";
import {
  resetDevicesState,
  fetchConfiguredDevices,
  fetchAvailableDevices,
  fetchMonitoredDevices,
} from "../../store/devicesSlice";
import {
  resetGatewaysState,
  fetchGateways,
  fetchAvailableGateways,
  fetchMonitoredGateways,
} from "../../store/gatewaysSlice";
import { AuthService } from "../../services/auth";
import { ApiService } from "../../services/api";
import { loadSettings } from "../../services/configParser";

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const { language } = useSelector((state: RootState) => state.ui);

  const translations = {
    en: {
      title: "Login to SwitchQ Map",
      email: "Email",
      password: "Password",
      submit: "Login",
      error: "Invalid credentials",
      loading: "Logging in...",
    },
    he: {
      title: "התחברות למפת SwitchQ",
      email: "אימייל",
      password: "סיסמה",
      submit: "התחבר",
      error: "פרטי התחברות שגויים",
      loading: "מתחבר...",
    },
  };

  const t = translations[language];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const settings = await loadSettings();
      const apiService = new ApiService(settings.endpoint);
      const authService = new AuthService(apiService);

      const result = await authService.login(email, password);

      // Reset all user-specific state to prevent cross-user data leakage
      dispatch(resetMapsState());
      dispatch(resetDevicesState());
      dispatch(resetGatewaysState());

      // Update auth state
      dispatch(loginSuccess(result));

      // Refetch data for the new user
      dispatch(fetchMaps());
      dispatch(fetchSharedMaps());
      dispatch(fetchConfiguredDevices());
      dispatch(fetchAvailableDevices());
      dispatch(fetchMonitoredDevices());
      dispatch(fetchGateways());
      dispatch(fetchAvailableGateways());
      dispatch(fetchMonitoredGateways());
    } catch (err) {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8 ${
        language === "he" ? "rtl" : "ltr"
      }`}
    >
      <div className="max-w-md w-full">
        <div className="card">
          <div className="text-center mb-8">
            <img
              src="/logo.png"
              alt="SwitchQ Logo"
              className="mx-auto h-16 w-16 mb-4"
            />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{t.title}</h2>
            <p className="text-gray-600">
              {language === "he"
                ? "היכנס לחשבון שלך כדי להמשיך"
                : "Sign in to your account to continue"}
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t.email}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input-field"
                  placeholder={
                    language === "he"
                      ? "הכנס את האימייל שלך"
                      : "Enter your email"
                  }
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t.password}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="input-field"
                  placeholder={
                    language === "he"
                      ? "הכנס את הסיסמה שלך"
                      : "Enter your password"
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex">
                  <svg
                    className="h-5 w-5 text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center"
            >
              {loading && (
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {loading ? t.loading : t.submit}
            </button>

            <div className="bg-primary-50 border border-primary-200 rounded-md p-4">
              <div className="flex">
                <svg
                  className="h-5 w-5 text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="ml-3">
                  <p className="text-sm text-primary-800">
                    {language === "he"
                      ? "השתמש בפרטי ההתחברות שלך ל-SwitchQ"
                      : "Use your SwitchQ credentials to log in"}
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
